<template>
  <div ref="strip" class="relative" @click="clear">
  <svg
    v-if="layout"
    @click="clear"
    :viewBox="`0 0 ${width} ${height}`"
    class="block w-full"
    role="img"
    :aria-label="summary"
  >
    <!-- Each number sits over its own rule. They collide only when the market
         is close to the frozen line, so the pair is pushed apart just enough to
         clear, rather than parked in a legend that makes you look twice. -->
    <g font-size="8.5" font-weight="600">
      <text
        v-for="label in labels"
        :key="label.key"
        :x="label.x" y="10"
        :fill="label.color"
        text-anchor="middle"
      >{{ label.text }}</text>
    </g>

    <!-- Key margins, behind everything: they are context, not data. -->
    <g>
      <line
        v-for="key in layout.keyLines"
        :key="`k${key.value}`"
        :x1="key.x" :x2="key.x" :y1="plotTop" :y2="axisY"
        stroke="#7dd3fc" stroke-opacity="0.16" stroke-width="1" stroke-dasharray="2 3"
      />
    </g>

    <!-- The edge itself: everything between our number and the consensus. -->
    <rect
      v-if="edgeBand"
      :x="edgeBand.x" :width="edgeBand.width"
      :y="axisY - 13" height="13"
      :fill="edgeColor" fill-opacity="0.16"
    />

    <!-- Full-bleed, not inset to `pad`. The padding exists to keep tick labels
         and dots off the edge, but an axis that stops short of the team names
         under its own ends reads as a line that got cut off rather than as a
         scale that runs between two teams. -->
    <line x1="0" :x2="width" :y1="axisY" :y2="axisY" stroke="#ffffff" stroke-opacity="0.18" />
    <g font-size="8" fill="#94a3b8" text-anchor="middle">
      <g v-for="tick in layout.ticks" :key="`t${tick.value}`">
        <line :x1="tick.x" :x2="tick.x" :y1="axisY" :y2="axisY + 3" stroke="#ffffff" stroke-opacity="0.18" />
        <text :x="tick.x" :y="axisY + 12">{{ formatSigned(tick.value) }}</text>
      </g>
    </g>

    <!-- Which team each end of the axis belongs to. The ticks are bare
         home-perspective numbers while the captions above name a team
         ("Frozen DAL -3"), so without this the reader has to negate every
         number on the axis to reconcile the two. Named once at the ends
         rather than on every tick, the same call the book table makes for
         its price column.
         `home_spread` ascends left to right, and a larger home number means
         the home team is getting more points — so low is the home team
         favoured and high is the away team. -->
    <g font-size="8" font-weight="600" fill="#64809c">
      <text x="0" :y="axisY + 12" text-anchor="start">{{ homeAbbr }}</text>
      <text :x="width" :y="axisY + 12" text-anchor="end">{{ awayAbbr }}</text>
    </g>

    <!-- Where each cohort of books sits, as a pair of rules in the same band
         the consensus uses — the gap between them is the whole point, and a
         gap is only legible against a shared baseline. Two hues, sky and
         violet, rather than the dots' one hue at two strengths: a 1.5px rule
         shaded halfway to the panel all but vanished on a phone, and the pair
         sit a few pixels apart, so they have to differ in kind to be told
         apart. Neither is the edge scale on purpose — these two say where the
         market is heading, not whether a number is good, and spending the
         green/amber axis on them would claim otherwise. -->
    <g v-if="cohortRules.length">
      <line
        v-for="rule in cohortRules"
        :key="rule.key"
        :x1="rule.x" :x2="rule.x" :y1="axisY - 9" :y2="axisY"
        :stroke="rule.color" stroke-width="1.5" stroke-linecap="round"
      />
    </g>

    <!-- Consensus: thinner and grey — where the market is, not a bet. -->
    <line
      v-if="layout.marketX != null"
      :x1="layout.marketX" :x2="layout.marketX" :y1="axisY - 13" :y2="axisY"
      :stroke="MARKET_COLOR" stroke-width="1.5"
    />

    <!-- The frozen line: the one number the pool actually hands us. -->
    <line
      v-if="layout.frozenX != null"
      :x1="layout.frozenX" :x2="layout.frozenX" :y1="plotTop" :y2="axisY + 4"
      :stroke="FROZEN_COLOR" stroke-width="2"
    />

    <!-- One dot per book, at its de-vigged number. A logo here instead would
         need twice the width, could not stack, and would spend the colour that
         currently carries the edge tier — the list below already answers "which
         book is this", so the dot only has to answer "where". -->
    <g
      v-for="dot in layout.dots"
      :key="dot.row.book.bookmaker"
      class="cursor-pointer"
      role="button"
      tabindex="0"
      :aria-label="bookLabel(dot.row)"
      @click.stop="toggle(dot)"
      @keydown.enter.prevent="toggle(dot)"
      @keydown.space.prevent="toggle(dot)"
      @mouseenter="hovered = dot.row.book.bookmaker"
      @mouseleave="hovered = null"
    >
      <!-- A 4px dot is not a touch target; this invisible disc is. -->
      <circle :cx="dot.x" :cy="dotY(dot)" r="11" fill="transparent" />
      <!-- Every dot is the same size and takes its cohort's colour — sky for
           sharp, violet for retail — so each cluster reads against its own
           cohort rule above. Colour used to repeat the edge (green/amber on
           `favour`), but position against the white frozen rule already says
           that; the one thing position cannot say is which side of it, at a
           glance, so a book that trails our number is drawn hollow. The
           green/amber scale stays on the row chips below. -->
      <!-- The anchor book gets a ring rather than a third fill state. It
           lands on exactly one dot of ten, in the dot's own hue, so it marks
           without competing with the cohort colour.

           The gap is wide on purpose, and it was tried the other way first. A
           hairline at `radius + 1.25` is cleaner on a desktop panel, but this
           strip is read on a phone, where the SVG scales down and a 0.75px
           stroke a pixel off the dot closes up into the dot's own edge — the
           mark disappears exactly where it is needed most. The wider ring
           survives that scaling, so it is the one that ships. -->
      <circle
        v-if="anchor(dot.row)"
        :cx="dot.x" :cy="dotY(dot)"
        :r="(isActive(dot) ? radius + 1.5 : radius) + 2.5"
        fill="none"
        :stroke="dotColor(dot.row)"
        :stroke-opacity="dimmed(dot) ? 0.15 : 0.4"
        stroke-width="1"
      />
      <circle
        :cx="dot.x" :cy="dotY(dot)"
        :r="isActive(dot) ? radius + 1.5 : radius"
        :fill="trails(dot.row) ? PANEL_COLOR : dotColor(dot.row)"
        :fill-opacity="dimmed(dot) ? 0.3 : 1"
        :stroke="isActive(dot) ? '#ffffff' : dotColor(dot.row)"
        :stroke-opacity="dimmed(dot) ? 0.3 : 0.95"
        :stroke-width="isActive(dot) || trails(dot.row) ? 1.5 : 1"
      />
    </g>
  </svg>

  <!-- Tapped book. Positioned in percentages because the SVG scales itself. -->
  <div
    v-if="activeDot"
    class="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md border border-white/15 bg-brand-navy/95 px-2 py-1 text-left shadow-lg"
    :style="tooltipStyle"
  >
    <p class="flex items-center gap-1.5 text-[11px] font-bold text-white">
      <img v-if="activeDot.row.book.logo" :src="activeDot.row.book.logo" alt="" class="h-3.5 w-3.5 rounded-sm object-contain" />
      {{ name(activeDot.row.book.bookmaker) }}
      <span v-if="sharp(activeDot.row)" class="sharp-tag">
        {{ anchor(activeDot.row) ? "sharp · anchor" : "sharp" }}
      </span>
    </p>
    <p class="mt-0.5 text-[10px] tabular-nums text-slate-300">
      posts {{ formatSigned(activeDot.row.line) }} at {{ price(activeDot.row.price) }}
    </p>
    <p class="text-[10px] tabular-nums" :style="{ color: edgeTone(activeDot.row) }">
      fair {{ formatSigned(activeDot.row.fairLine) }}
    </p>
  </div>
  </div>
</template>

<script>
import { formatLine, formatSigned, isAnchorBook, isSharpBook } from "../utils/edge.js";
import { stripLayout, STRIP_WIDTH, STRIP_PAD, DOT_RADIUS } from "../utils/marketStrip.js";
import { BOOKMAKER_NAMES } from "../utils/bookmakerNames.js";
import { edgeTone } from "../utils/edgeTone.js";

// Borrowed from EdgeVerdict, which draws the same two numbers on every card:
// white is the number the pool froze for us, brand green is where the market
// is now. Two views of one comparison must not swap their colours.
const FROZEN_COLOR = "#ffffff";
const MARKET_COLOR = "#34ef93";
// The same sky the sharp tag uses, in the strip and in the table's legend.
export const SHARP_COLOR = "#7dd3fc";
// Retail's cohort rule and dots, and its legend swatch in BookmakerTable.
export const RETAIL_COLOR = "#c4b5fd";
// The panel behind the strip; a hollow dot is filled with it so it still
// covers the dots stacked beneath it.
const PANEL_COLOR = "#16304c";
export default {
  name: "MarketStrip",
  emits: ["select"],
  props: {
    frozen: { type: Number, default: null },
    market: { type: Number, default: null },
    rows: { type: Array, default: () => [] },
    // Every number here is home-perspective, the same as the tug bar above it
    // and the same as the database — so the strip is a zoom of that bar rather
    // than a second chart, and the two never appear to move opposite ways.
    // The abbreviations are only for naming a number ("LAC -12.5"); which one
    // gets named follows the sign, so a favourite-flip shows as a change of
    // team exactly as it does on the bar.
    homeAbbr: { type: String, default: "" },
    awayAbbr: { type: String, default: "" },
    // +1 when the edge is on the home team, -1 when it is on the away team.
    // The band's colour cannot be read off `frozen > market` any more: in
    // home-perspective terms a move worth something to the away team makes the
    // frozen number the *smaller* of the two.
    edgeDirection: { type: Number, default: 1 },
    // The row the list is pointing at, so hovering a book down there lights up
    // its dot up here. The two views are of the same ten numbers; keeping them
    // linked is most of what makes the strip readable.
    highlight: { type: String, default: null },
    // The two cohort means, from `sharpSplit`. Passed in rather than derived
    // here because the sentence under the strip reads the identical numbers,
    // and two views of one comparison must not compute it twice.
    sharpMean: { type: Number, default: null },
    retailMean: { type: Number, default: null },
  },
  data() {
    return {
      FROZEN_COLOR,
      MARKET_COLOR,
      PANEL_COLOR,
      containerWidth: STRIP_WIDTH,
      pad: STRIP_PAD,
      radius: DOT_RADIUS,
      plotTop: 17,
      axisY: 34,
      selected: null,
      hovered: null,
    };
  },
  computed: {
    // Keep the full horizontal span, but cap the magnification of text and
    // dots at 1.5x. Narrow phone panels retain their original proportions.
    width() {
      return Math.max(STRIP_WIDTH, this.containerWidth / 1.5);
    },
    layout() {
      return stripLayout({ frozen: this.frozen, market: this.market, books: this.rows, width: this.width });
    },
    // Grows with the deepest stack of books, so a cluster is never clipped.
    height() {
      return this.axisY + 28 + (this.layout ? this.layout.levels : 1) * 10;
    },
    // Rough text metrics are enough here: the font is fixed, the strings are
    // short, and the only decision is whether two of them touch.
    labels() {
      const out = [];
      const half = (text) => (text.length * 4.5) / 2;
      if (this.layout?.frozenX != null) {
        out.push({ key: "frozen", x: this.layout.frozenX, color: FROZEN_COLOR, text: `Frozen ${this.line(this.frozen)}` });
      }
      if (this.layout?.marketX != null) {
        out.push({ key: "market", x: this.layout.marketX, color: MARKET_COLOR, text: `Market ${this.line(this.market)}` });
      }
      if (out.length === 2) {
        const [a, b] = out[0].x <= out[1].x ? out : [out[1], out[0]];
        const needed = half(a.text) + half(b.text) + 5;
        const overlap = needed - (b.x - a.x);
        if (overlap > 0) {
          a.x -= overlap / 2;
          b.x += overlap / 2;
        }
      }
      // Keep them on screen; a label pinned at the edge still points at the
      // only rule near that edge.
      return out.map((label) => ({
        ...label,
        x: Math.min(this.width - half(label.text) - 2, Math.max(half(label.text) + 2, label.x)),
      }));
    },
    // Positioned through the layout's own scale, so they cannot drift off the
    // dots they summarise.
    cohortRules() {
      if (!this.layout) return [];
      const out = [];
      if (Number.isFinite(this.sharpMean)) {
        out.push({ key: "sharp", x: this.layout.x(this.sharpMean), color: SHARP_COLOR });
      }
      if (Number.isFinite(this.retailMean)) {
        out.push({ key: "retail", x: this.layout.x(this.retailMean), color: RETAIL_COLOR });
      }
      return out;
    },
    edgeBand() {
      const { frozenX, marketX } = this.layout || {};
      if (frozenX == null || marketX == null) return null;
      return { x: Math.min(frozenX, marketX), width: Math.abs(marketX - frozenX) };
    },
    // The band between the two rules is the edge. Green when the frozen line
    // beats the market, amber when it trails — the band is the only thing here
    // with a direction; the two rules keep their fixed identities.
    edgeColor() {
      if (this.frozen == null || this.market == null) return MARKET_COLOR;
      const held = (this.frozen - this.market) * (this.edgeDirection < 0 ? -1 : 1);
      if (held === 0) return MARKET_COLOR;
      return held > 0 ? "#34ef93" : "#e08a3c";
    },
    // Emphasis and the tooltip are NOT the same question. A row hovered down in
    // the list should light its dot; it must not pop a tooltip, and — the bug
    // this separation fixes — it must not keep one open after the tap that
    // opened it has been dismissed, since selecting a dot sets `highlight` via
    // the parent and it would feed straight back in here.
    activeKey() {
      return this.tooltipKey || this.highlight;
    },
    // Tap wins over hover, so a tapped dot survives the pointer moving away.
    tooltipKey() {
      return this.selected || this.hovered;
    },
    activeDot() {
      if (!this.tooltipKey || !this.layout) return null;
      return this.layout.dots.find((dot) => dot.row.book.bookmaker === this.tooltipKey) || null;
    },
    tooltipStyle() {
      if (!this.activeDot) return {};
      return {
        left: `${(this.activeDot.x / this.width) * 100}%`,
        top: `${((this.dotY(this.activeDot) - this.radius - 3) / this.height) * 100}%`,
      };
    },
    summary() {
      if (this.frozen == null) return "No frozen line yet";
      if (this.market == null) return `Frozen line ${this.line(this.frozen)}; no market yet`;
      const cohorts =
        Number.isFinite(this.sharpMean) && Number.isFinite(this.retailMean)
          ? `; sharp books average ${this.line(this.sharpMean)} and retail books ${this.line(this.retailMean)}`
          : "";
      return `Frozen line ${this.line(this.frozen)} against a market of ${this.line(this.market)}, with ${this.layout?.dots.length || 0} books plotted at the numbers they post${cohorts}`;
    },
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      // Updating the SVG changes this container's height. Defer that write
      // until the next frame, outside ResizeObserver's delivery cycle, and
      // ignore height-only notifications to avoid a layout feedback loop.
      cancelAnimationFrame(this.resizeFrame);
      if (width <= 0 || Math.abs(width - this.containerWidth) < 0.5) return;
      this.resizeFrame = requestAnimationFrame(() => {
        this.containerWidth = width;
      });
    });
    this.resizeObserver.observe(this.$refs.strip);
    // Tapping the strip's own background clears the selection; this covers the
    // rest of the page, where a stuck tooltip is most annoying.
    document.addEventListener("click", this.dismissFromOutside);
  },
  beforeUnmount() {
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.resizeFrame);
    document.removeEventListener("click", this.dismissFromOutside);
  },
  methods: {
    formatSigned,
    // Team-first, the same helper the tug bar's captions use, so the two views
    // print the identical string for the identical number.
    line(value) {
      return formatLine(value, this.homeAbbr, this.awayAbbr);
    },
    sharp(row) {
      return isSharpBook(row.book.bookmaker);
    },
    // The heaviest book in the consensus — the biggest single reason the
    // market tick sits where it does.
    anchor(row) {
      return isAnchorBook(row.book.bookmaker);
    },
    dismissFromOutside(event) {
      // With no layout the root is a comment node, which has no `contains`.
      if (!this.selected || typeof this.$el?.contains !== "function") return;
      if (!this.$el.contains(event.target)) this.clear();
    },
    // Clears the highlight the parent is holding too, not just our own state.
    clear() {
      this.selected = null;
      this.hovered = null;
      this.$emit("select", null);
    },
    dotY(dot) {
      return this.axisY + 22 + dot.level * 10;
    },
    isActive(dot) {
      return this.activeKey === dot.row.book.bookmaker;
    },
    // Only once something is picked — an untouched strip stays at full strength.
    dimmed(dot) {
      return this.activeKey != null && !this.isActive(dot);
    },
    toggle(dot) {
      const key = dot.row.book.bookmaker;
      this.selected = this.selected === key ? null : key;
      this.$emit("select", this.selected);
    },
    name(bookmaker) {
      const key = String(bookmaker || "").toLowerCase();
      return BOOKMAKER_NAMES[key] || bookmaker;
    },
    price(value) {
      return value == null ? "no price" : formatSigned(value, 0);
    },
    // Hue carries the cohort, matching the cohort rules above.
    dotColor(row) {
      return this.sharp(row) ? SHARP_COLOR : RETAIL_COLOR;
    },
    // A book whose de-vigged number sits on the wrong side of ours — the
    // amber rows below. A tie is neither side, as in `bookTally`.
    trails(row) {
      return row.favour != null && row.favour < -1e-9;
    },
    edgeTone,
    bookLabel(row) {
      const tag = this.anchor(row) ? " (sharp, heaviest weighted)" : this.sharp(row) ? " (sharp)" : "";
      return `${this.name(row.book.bookmaker)}${tag}: posts ${formatSigned(row.line)}, fair ${formatSigned(row.fairLine)}`;
    },
  },
};
</script>

<style scoped>
/* The sharp-book tag. */
.sharp-tag {
  border-radius: 999px;
  background: rgb(56 189 248 / 0.14);
  padding: 0.05rem 0.35rem;
  font-size: 0.55rem;
  font-weight: 800;
  color: #7dd3fc;
  text-transform: uppercase;
}
</style>
